import { Box } from "@mui/material";
import type { FC } from "react";

import { FONT_SIZE, INK, NEUTRAL, STATUS_BADGE } from "../../../../theme/tokens";
import { MetricStatus, type MetricThresholds, resolveMetricStatus } from "./metricThresholds";

/**
 * Wash/ink pair per resolved status. Reuses the shared `STATUS_BADGE` traffic-light tokens so the
 * cell coloring stays on the app's one palette; `Neutral` is the muted "no rule applies" pill.
 */
const STATUS_PALETTE: Record<MetricStatus, { bg: string; ink: string }> = {
	[MetricStatus.Critical]: { bg: STATUS_BADGE.ERROR_BG, ink: STATUS_BADGE.ERROR_INK },
	[MetricStatus.Warning]: { bg: STATUS_BADGE.WARN_BG, ink: STATUS_BADGE.WARN_INK },
	[MetricStatus.Good]: { bg: STATUS_BADGE.SUCCESS_BG, ink: STATUS_BADGE.SUCCESS_INK },
	[MetricStatus.Neutral]: { bg: NEUTRAL[100], ink: INK.MUTED },
};

interface Props {
	value: number | null | undefined;
	/** Render the numeric value as display text (e.g. append "%"). Not called for missing values. */
	formatValue: (value: number) => string;
	/**
	 * Ascending bands that map `value` to a status. Use for single-value coloring. Ignored when
	 * `status` is supplied.
	 */
	thresholds?: MetricThresholds;
	/**
	 * A pre-resolved status, for coloring that can't be derived from `value` alone — e.g. relative to
	 * another column (actual vs. target). Takes precedence over `thresholds`. `value` still drives the
	 * displayed text and the missing-value em dash.
	 */
	status?: MetricStatus;
	dataTestId?: string;
}

/**
 * A compact traffic-light pill for a threshold-colored numeric metric cell. Fixed-threshold sibling
 * of `TrendBadge` — same contained pill shape, but the color comes from declared bands (or a
 * pre-resolved `status`) rather than percentile cutoffs. The value stays visible as text, so the
 * signal never rests on color alone. Missing values render a muted em dash. Drop it into a
 * `SystemDataGrid` column's `renderCell`.
 */
export const MetricThresholdBadge: FC<Props> = ({ value, formatValue, thresholds, status, dataTestId }) => {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return (
			<Box data-testid={dataTestId} sx={{ color: NEUTRAL[400], fontSize: FONT_SIZE.CAPTION, fontWeight: 500 }}>
				—
			</Box>
		);
	}

	const resolvedStatus = status ?? (thresholds ? resolveMetricStatus(value, thresholds) : MetricStatus.Neutral);
	const palette = STATUS_PALETTE[resolvedStatus];

	return (
		<Box
			data-testid={dataTestId}
			data-status={resolvedStatus}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				minWidth: 52,
				px: 0.75,
				py: 0.25,
				borderRadius: 999,
				backgroundColor: palette.bg,
				color: palette.ink,
				fontSize: FONT_SIZE.CAPTION,
				fontWeight: 600,
				lineHeight: 1.3,
				fontVariantNumeric: "tabular-nums",
				whiteSpace: "nowrap",
			}}
		>
			{formatValue(value)}
		</Box>
	);
};
